import React, { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import { t } from 'i18next';

import { Box, Divider, IconButton, Popover } from '@mui/material';

import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@datanomik/commons-ui';

import { ArrowRightSLine } from '@datanomik/commons-ui/icons';

import type { SidebarMenuItemConfig, SidebarSubItemConfig } from '../../types';
import SidebarSubItem from './SidebarSubItem';

const SIDEBAR_CLASS = 'DnkSidebar-root';
const SIDEBAR_OPEN_CLASS = 'Dnk-open';

type Props = {
  item: SidebarMenuItemConfig;
  visibleChildren: SidebarSubItemConfig[];
  isActive: (path: string) => boolean;
  isSubItemActive: (parentPath: string, childPath: string, fullPath?: string) => boolean;
  createUrl: (path: string) => string;
  onAction?: (action: string) => void;
};

const SidebarMenuGroup = ({
  item,
  visibleChildren,
  isActive,
  isSubItemActive,
  createUrl,
  onAction,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidebarEl = useRef<HTMLElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);

  const open = Boolean(anchorEl);

  const keepSidebarOpen = (el: HTMLElement) => {
    const sidebar = el.closest(`.${SIDEBAR_CLASS}`) as HTMLElement | null;
    if (sidebar && !sidebar.classList.contains(SIDEBAR_OPEN_CLASS)) {
      sidebar.classList.add(SIDEBAR_OPEN_CLASS);
      sidebarEl.current = sidebar;
    }
  };

  const releaseSidebar = () => {
    if (sidebarEl.current) {
      sidebarEl.current.classList.remove(SIDEBAR_OPEN_CLASS);
      sidebarEl.current = null;
    }
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => {
      setAnchorEl(null);
      releaseSidebar();
    }, 120);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const cancelOpen = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
  };

  const handleRowEnter = () => {
    cancelClose();
    cancelOpen();
    openTimer.current = setTimeout(() => {
      if (rowRef.current) {
        keepSidebarOpen(rowRef.current);
        setAnchorEl(rowRef.current);
      }
    }, 350);
  };

  const handleRowLeave = () => {
    cancelOpen();
    scheduleClose();
  };
  const handlePopoverEnter = () => cancelClose();
  const handlePopoverLeave = () => scheduleClose();

  useEffect(() => () => {
    cancelOpen();
    cancelClose();
    releaseSidebar();
  }, []);

  const isParentActive = isActive(item.path);
  const hasActiveChild = visibleChildren.some(
    (child) => isSubItemActive(item.path, child.path, child.fullPath),
  );

  const firstChild = visibleChildren[0];
  const mainTo = firstChild?.fullPath
    ? createUrl(`/${firstChild.fullPath}`)
    : firstChild?.path
      ? createUrl(`/${item.path}/${firstChild.path}`)
      : createUrl(`/${item.path}`);

  return (
    <>
      <Box ref={rowRef} onMouseEnter={handleRowEnter} onMouseLeave={handleRowLeave}>
        <ListItem sx={{ py: 0 }}>
          <ListItemButton
            component={Link}
            to={mainTo}
            selected={isParentActive || hasActiveChild}
            sx={{ pr: 0.5, py: '7px' }}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={t(item.sidebarGroupLabel ?? item.label ?? '')} primaryTypographyProps={{ fontSize: '13px' }} />

            {item.isNew && (
              <Chip label={t('New')} sx={{ backgroundColor: 'teal.light', color: 'teal.darker' }} />
            )}
            {item.isBeta && (
              <Chip label="Beta" sx={{ backgroundColor: 'warning.lighter', color: 'warning.darker' }} />
            )}

            <IconButton
              className="sidebar-group-toggle"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                ml: 'auto',
                p: 0.25,
                color: 'inherit',
                flexShrink: 0,
                width: 20,
                height: 20,
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                '& svg': { fontSize: '14px' },
              }}
            >
              <ArrowRightSLine />
            </IconButton>
          </ListItemButton>
        </ListItem>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        disableAutoFocus
        PaperProps={{
          onMouseEnter: handlePopoverEnter,
          onMouseLeave: handlePopoverLeave,
          sx: {
            minWidth: 200,
            boxShadow: '0px 8px 24px rgba(0,0,0,0.12), 0px 2px 8px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            overflow: 'hidden',
            ml: '4px',
          },
        }}
        sx={{ pointerEvents: 'none' }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          {/* Header */}
          <Box
            sx={{
              px: 2,
              pt: 1.5,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {item.icon && (
              <Box
                sx={{
                  display: 'flex',
                  color: 'primary.main',
                  '& svg': { fontSize: '15px !important' },
                  opacity: 0.8,
                }}
              >
                {item.icon}
              </Box>
            )}
            <Box
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: 'neutral.500',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                opacity: 0.6,
              }}
            >
              {t(item.sidebarGroupLabel ?? item.label ?? '')}
            </Box>
          </Box>

          <Divider sx={{ mx: 1.5, borderColor: 'stroke.200' }} />

          <List sx={{ padding: '6px 6px' }}>
            {visibleChildren.map((child: SidebarSubItemConfig) => {
              const baseTo = child.fullPath
                ? createUrl(`/${child.fullPath}`)
                : child.path
                  ? createUrl(`/${item.path}/${child.path}`)
                  : createUrl(`/${item.path}`);
              const childTo = child.queryParam ? `${baseTo}?${child.queryParam}` : baseTo;

              return (
                <SidebarSubItem
                  key={child.key}
                  label={child.label}
                  to={child.action ? undefined : childTo}
                  action={child.action}
                  isSelected={!child.action && isSubItemActive(item.path, child.path ?? '', child.fullPath, child.queryParam)}
                  inPopover
                  onAction={onAction}
                />
              );
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default SidebarMenuGroup;
